import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Pill, Search, ShieldQuestion, Trash2, UserRound } from 'lucide-react';
import {
  PHARMACIST_SELECTION_REASONS,
  PHARMACIST_SELECTION_REASON_LABELS,
  type IntegrationCallOutcomeDto,
  type PharmacistSelectionReason,
  type PreAuthRequestResultDto,
  type PreAuthSourcesDto,
} from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, Input, Select, Spinner } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { describeIntegrationError, reportOutcome } from '../integrations/external-outcome';

interface CoverageOption {
  payerId: string;
  payer: { id: string; name: string };
  isActive: boolean;
}

interface LineChoice {
  daysSupply: string;
  selectionReason: PharmacistSelectionReason | '';
}

interface WalkInLine extends LineChoice {
  productId: string;
  label: string;
  quantity: string;
}

interface ProductHit {
  id: string;
  genericName: string;
  brandName: string | null;
  strength: string | null;
}

interface IcdHit {
  code: string;
  title: string;
}

export type PharmacyPreAuthTarget =
  | { mode: 'prescription'; patientId: string; prescriptionId: string; storeId?: string }
  | { mode: 'walk-in'; storeId: string; patientId?: undefined; prescriptionId?: undefined };

function ReasonSelect({ value, onChange, id }: { value: PharmacistSelectionReason | ''; onChange: (value: PharmacistSelectionReason | '') => void; id: string }) {
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value as PharmacistSelectionReason | '')}>
      <option value="">No reason given</option>
      {PHARMACIST_SELECTION_REASONS.map((reason) => (
        <option key={reason} value={reason}>
          {PHARMACIST_SELECTION_REASON_LABELS[reason]}
        </option>
      ))}
    </Select>
  );
}

function wholeDays(value: string): number | undefined {
  const days = Number(value);
  return Number.isInteger(days) && days >= 1 && days <= 365 ? days : undefined;
}

export function PharmacyPreAuthPage({ target, returnTo }: { target: PharmacyPreAuthTarget; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [patientId, setPatientId] = useState(target.patientId ?? '');
  const [payerId, setPayerId] = useState('');
  const [choices, setChoices] = useState<Record<string, LineChoice>>({});
  const [diagnosisTerm, setDiagnosisTerm] = useState('');
  const [diagnosis, setDiagnosis] = useState<IcdHit | null>(null);
  const [productTerm, setProductTerm] = useState('');
  const [walkInLines, setWalkInLines] = useState<WalkInLine[]>([]);

  const coverages = useQuery({
    queryKey: ['patient-coverages', patientId],
    queryFn: () => get<CoverageOption[]>(`/insurance/patients/${patientId}/coverages`),
    enabled: Boolean(patientId),
  });
  const active = (coverages.data ?? []).filter((coverage) => coverage.isActive);

  const sources = useQuery({
    queryKey: ['pharmacy-preauth-sources', patientId, payerId, target.prescriptionId],
    queryFn: () => get<PreAuthSourcesDto>(`/integrations/nphies/pre-auth-sources${qs({ patientId, payerId, prescriptionId: target.prescriptionId })}`),
    enabled: target.mode === 'prescription' && Boolean(patientId && payerId),
  });

  const icd = useQuery({
    queryKey: ['icd', diagnosisTerm.trim()],
    queryFn: () => get<IcdHit[]>(`/icd/search${qs({ q: diagnosisTerm.trim(), limit: 8 })}`),
    enabled: target.mode === 'walk-in' && !diagnosis && diagnosisTerm.trim().length >= 2,
    staleTime: 10 * 60_000,
  });

  const products = useQuery({
    queryKey: ['pharmacy-search', target.storeId, productTerm.trim()],
    queryFn: () => get<ProductHit[]>(`/pharmacy/search${qs({ storeId: target.storeId, q: productTerm.trim(), limit: 10 })}`),
    enabled: target.mode === 'walk-in' && Boolean(target.storeId) && productTerm.trim().length >= 2,
  });

  const choiceOf = (key: string): LineChoice => choices[key] ?? { daysSupply: '', selectionReason: '' };
  const setChoice = (key: string, patch: Partial<LineChoice>) => setChoices((current) => ({ ...current, [key]: { ...choiceOf(key), ...patch } }));
  const selectable = (sources.data?.lines ?? []).filter((line) => line.selectable);

  const walkInReady = Boolean(diagnosis) && walkInLines.length > 0 && walkInLines.every((line) => wholeDays(line.daysSupply) && Number(line.quantity) > 0);
  const ready = Boolean(patientId && payerId) && (target.mode === 'prescription' ? selectable.length > 0 : walkInReady);

  const send = useMutation({
    mutationFn: () =>
      target.mode === 'prescription'
        ? post<PreAuthRequestResultDto>('/integrations/nphies/pre-auths/request', {
            patientId,
            payerId,
            prescriptionId: target.prescriptionId,
            send: true,
            lines: selectable.map((line) => {
              const choice = choiceOf(line.sourceKey);
              return {
                sourceType: line.sourceType,
                sourceId: line.sourceId,
                ...(wholeDays(choice.daysSupply) ? { daysSupply: wholeDays(choice.daysSupply) } : {}),
                ...(choice.selectionReason ? { selectionReason: choice.selectionReason } : {}),
              };
            }),
          })
        : post<PreAuthRequestResultDto>('/integrations/nphies/pre-auths/pharmacy-walk-in', {
            patientId,
            payerId,
            diagnosis: diagnosis ? `${diagnosis.code} - ${diagnosis.title}` : '',
            send: true,
            lines: walkInLines.map((line) => ({
              productId: line.productId,
              quantity: Number(line.quantity),
              daysSupply: wholeDays(line.daysSupply),
              ...(line.selectionReason ? { selectionReason: line.selectionReason } : {}),
            })),
          }),
    onSuccess: (result) => {
      if (result.sent) reportOutcome(toast, result.sent as IntegrationCallOutcomeDto, 'The insurer has answered');
      else toast.success('Saved as a draft request');
      navigate(returnTo, { replace: true });
    },
    onError: (error) => toast.error('Could not ask the insurer', describeIntegrationError(error)),
  });

  const dirty = !send.isSuccess && (patientId !== (target.patientId ?? '') || Boolean(payerId) || Boolean(diagnosis) || walkInLines.length > 0 || Object.keys(choices).length > 0);

  return (
    <FormScreen
      title="Ask the insurer"
      description={target.mode === 'prescription' ? 'A pharmacy pre-authorisation for the medicines still to be dispensed on this prescription' : 'A pharmacy pre-authorisation for a prescription written outside this facility'}
      icon={ShieldQuestion}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) send.mutate();
      }}
    >
      <FormSection title={tr('billing', 'patient')} icon={UserRound}>
        {target.mode === 'walk-in' && (
          <Field label="Patient" required hint="The insurer identifies the patient by national ID or Iqama">
            <PatientPicker value={patientId} onChange={setPatientId} />
          </Field>
        )}
        {patientId && (
          <Field label="Insurer" htmlFor="pharmacy-preauth-payer" required>
            <Select id="pharmacy-preauth-payer" value={payerId} onChange={(e) => setPayerId(e.target.value)}>
              <option value="">Choose the patient’s insurer</option>
              {active.map((coverage) => (
                <option key={coverage.payerId} value={coverage.payerId}>
                  {coverage.payer.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {patientId && coverages.isSuccess && active.length === 0 && (
          <Alert variant="warning" className="sm:col-span-2">
            This patient has no active insurance policy on file.
          </Alert>
        )}
      </FormSection>

      <FormSection title={tr('billing', 'medicines')} icon={Pill} columns={1}>
        {target.mode === 'prescription' && payerId && (
          <div className="space-y-2">
            {sources.isLoading && <Spinner />}
            {sources.data && selectable.length === 0 && <Alert>Every medicine on this prescription already has an answer or has been dispensed.</Alert>}
            {selectable.map((line) => (
              <div key={line.sourceKey} className="grid gap-2 rounded-md border p-2 sm:grid-cols-3">
                <div className="text-sm">
                  <p className="font-medium">{line.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {line.quantity} · {line.code ?? 'no insurer code'}
                  </p>
                </div>
                <Field label="Days of supply" htmlFor={`days-${line.sourceKey}`} hint="Leave blank to use the prescription’s duration">
                  <Input id={`days-${line.sourceKey}`} type="number" min={1} max={365} value={choiceOf(line.sourceKey).daysSupply} onChange={(e) => setChoice(line.sourceKey, { daysSupply: e.target.value })} />
                </Field>
                <Field label="Why this product" htmlFor={`reason-${line.sourceKey}`}>
                  <ReasonSelect id={`reason-${line.sourceKey}`} value={choiceOf(line.sourceKey).selectionReason} onChange={(selectionReason) => setChoice(line.sourceKey, { selectionReason })} />
                </Field>
              </div>
            ))}
          </div>
        )}
        {target.mode === 'prescription' && !payerId && <p className="text-sm text-muted-foreground">Choose the insurer to see the medicines still to be dispensed.</p>}

        {target.mode === 'walk-in' && payerId && (
          <div className="space-y-3">
            <Field label="Diagnosis" required hint="Pick it from the ICD-10 list">
              {diagnosis ? (
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-xs text-primary">{diagnosis.code}</span>
                  <span className="flex-1">{diagnosis.title}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setDiagnosis(null)}>
                    Change
                  </Button>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="relative">
                    <Search className="pointer-events-none absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input className="ps-8" value={diagnosisTerm} onChange={(e) => setDiagnosisTerm(e.target.value)} placeholder="Code or description" />
                  </div>
                  {(icd.data ?? []).map((hit) => (
                    <button key={hit.code} type="button" className="block w-full rounded px-2 py-1 text-start text-sm hover:bg-accent focus-ring" onClick={() => setDiagnosis(hit)}>
                      <span className="font-mono text-xs text-primary">{hit.code}</span> {hit.title}
                    </button>
                  ))}
                </div>
              )}
            </Field>
            <Field label="Medicines" required>
              <div className="space-y-2">
                <Input value={productTerm} onChange={(e) => setProductTerm(e.target.value)} placeholder="Search a medicine in this store" />
                {(products.data ?? []).map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    className="block w-full rounded px-2 py-1 text-start text-sm hover:bg-accent focus-ring"
                    onClick={() => {
                      setWalkInLines((current) =>
                        current.some((line) => line.productId === product.id)
                          ? current
                          : [...current, { productId: product.id, label: [product.brandName ?? product.genericName, product.strength].filter(Boolean).join(' '), quantity: '1', daysSupply: '', selectionReason: '' }],
                      );
                      setProductTerm('');
                    }}
                  >
                    {product.brandName ?? product.genericName} {product.strength ?? ''}
                  </button>
                ))}
                {walkInLines.map((line, index) => (
                  <div key={line.productId} className="grid items-end gap-2 rounded-md border p-2 sm:grid-cols-5">
                    <p className="text-sm font-medium sm:col-span-2">{line.label}</p>
                    <Field label="Quantity" htmlFor={`qty-${line.productId}`}>
                      <Input id={`qty-${line.productId}`} type="number" min={1} value={line.quantity} onChange={(e) => setWalkInLines((current) => current.map((row, i) => (i === index ? { ...row, quantity: e.target.value } : row)))} />
                    </Field>
                    <Field label="Days of supply" htmlFor={`walk-days-${line.productId}`} required>
                      <Input id={`walk-days-${line.productId}`} type="number" min={1} max={365} value={line.daysSupply} onChange={(e) => setWalkInLines((current) => current.map((row, i) => (i === index ? { ...row, daysSupply: e.target.value } : row)))} />
                    </Field>
                    <div className="flex items-end gap-1">
                      <ReasonSelect id={`walk-reason-${line.productId}`} value={line.selectionReason} onChange={(selectionReason) => setWalkInLines((current) => current.map((row, i) => (i === index ? { ...row, selectionReason } : row)))} />
                      <Button type="button" variant="ghost" size="icon" aria-label="Remove" onClick={() => setWalkInLines((current) => current.filter((_, i) => i !== index))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Field>
          </div>
        )}
        {target.mode === 'walk-in' && !payerId && <p className="text-sm text-muted-foreground">Choose the patient and the insurer first.</p>}
        <p className="text-xs text-muted-foreground">Wasfaty prescriptions from the national hub are still dispensed through the Wasfaty button; this request goes to the patient’s insurer through NPHIES.</p>
      </FormSection>

      <FormActions submitLabel="Send to the insurer" loading={send.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
